import { computed, ref, watch, type Ref } from 'vue'
import { tooLongHashtags } from '@/todos/hashtags'
import {
  accepted,
  escape,
  initialNav,
  moveSelection,
  reconcile,
  reopen,
  selectedIndex,
  typed,
} from '@/todos/suggestNav'
import { hashtagAt, suggestHashtags } from '@/todos/tags'

export interface HashtagCompletionSources {
  text: Ref<string> // (for a chip editor the "logic text": a chip counts as one space)
  caret: Ref<number>
  focused: Ref<boolean>
  disabled: () => boolean
  names: () => string[] // (the candidates: own tags, aliases, tags in use)
  known: () => string[] // (the tags in use, most used first)
}

// The hashtag completion of an add field, apart from how the field is built: while a hashtag is being typed (`#word`,
// `#"Two words"`, `#[Two words]`) the most used tags are offered, narrowed by what is typed. The keyboard state is
// `todos/suggestNav.ts`: nothing is selected when the list opens, Escape closes it and clears the selection, typing keeps a
// selection that is still listed, Tab reopens a closed list. The field wires its events to the handlers and does the
// picking itself (a chip in the one-line editor, text in a textarea), with the name `accept` or `onTab` hand back.
export function useHashtagCompletion(sources: HashtagCompletionSources) {
  const nav = ref(initialNav)
  const hashtag = computed(() => hashtagAt(sources.text.value, sources.caret.value))
  // (the whole tag around the caret is searched, so moving the caret inside it does not change the list)
  const matches = computed(() =>
    hashtag.value && !sources.disabled()
      ? suggestHashtags(sources.names(), sources.known(), hashtag.value.full)
      : [],
  )
  const matchNames = computed(() => matches.value.map((match) => match.name))
  const active = computed(() => selectedIndex(nav.value, matchNames.value))
  watch(matchNames, (shown) => {
    nav.value = reconcile(nav.value, shown)
  })
  // A tag over the length limit is left as plain text (never cut), so say so.
  const tooLongTag = computed(() => tooLongHashtags(sources.text.value).length > 0)
  const open = computed(
    () => sources.focused.value && !nav.value.closed && matches.value.length > 0,
  )

  // Down and Up move the selection while the list is open; the caret and the text stay as they are.
  function move(step: 1 | -1, event: Event): void {
    if (!open.value) return
    event.preventDefault()
    nav.value = moveSelection(nav.value, matchNames.value, step)
  }

  // The name Enter takes: only while the list is open and something is selected.
  function accept(): string | null {
    return open.value ? accepted(nav.value, matchNames.value) : null
  }

  // Tab takes the selected entry too (returned for the field to pick), and on a list that Escape closed it opens a fresh
  // search while the caret is in a hashtag. Anywhere else (and with Shift) it keeps its normal job.
  function onTab(event: KeyboardEvent): string | null {
    if (event.shiftKey) return null
    const name = accept()
    if (name !== null) {
      event.preventDefault()
      return name
    }
    if (nav.value.closed && hashtag.value) {
      event.preventDefault()
      nav.value = reopen()
    }
    return null
  }

  // Escape closes the list and drops the selection; while the list was open it is not meant for anything around the field.
  function onEscape(event: Event): void {
    if (open.value) event.stopPropagation()
    nav.value = escape(nav.value)
  }

  return {
    hashtag,
    matches,
    active,
    open,
    tooLongTag,
    move,
    accept,
    onTab,
    onEscape,
    close: (): void => {
      nav.value = escape(nav.value)
    },
    typed: (): void => {
      nav.value = typed(nav.value)
    },
    reset: (): void => {
      nav.value = initialNav
    },
  }
}
